import { useEffect, useRef, useState } from 'react';
import {
    Button,
    Col,
    Form,
    OverlayTrigger,
    Popover,
    Row,
    Spinner,
    InputGroup,
    Card,
} from 'react-bootstrap';
import { FaInfo, FaEye, FaEyeSlash, FaUserPlus, FaCheckCircle } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import FormContainer from '../../components/form/FormContainer';
import Loader from '../../components/layout/Loader';
import Meta from '../../components/layout/Meta';
import {
    useCreateContificoPersonClientMutation,
    useGetContificoPersonDataMutation,
} from '../../slices/contificoPersonApiSlice';
import { selectIdIsForeigner, selectIdPersonType, selectIdType } from '../../slices/idSlice';
import { useRegisterMutation } from '../../slices/userApiSlice';
import { setCredentials } from '../../slices/authSlice';
import { fetchActiveGlobalDiscount } from '../../slices/activeGlobalDiscountSlice';
import TermsAndConditionsModal from '../../components/user/TermsAndConditionsModal';
import zxcvbn from 'zxcvbn';
import DocIdGroup from '../../components/user/DocIdGroup';
import { setFinalDocId } from '../../utils/docIdUtils';
import { createContificoClientIfNotExists, fillFromContifico } from '../../utils/userUtils';
import { sanitizeInput } from '../../utils/formUtils';
import { clearGuestId } from '../../utils/sessionUtils';
import { orderApiSlice } from '../../slices/orderApiSlice';

const RegisterScreen = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const idTypeSelect = useSelector(selectIdType);
    const idPersonTypeSelect = useSelector(selectIdPersonType);
    const idIsForeignerSelect = useSelector(selectIdIsForeigner);

    const infoButtonRef = useRef(null);

    const [docId, setDocId] = useState('');
    const [validDocId, setValidDocId] = useState(false);
    const [type, setType] = useState(idPersonTypeSelect);
    const [taxDocId, setTaxDocId] = useState('');
    const [isClient, setIsClient] = useState(true);
    const [isForeigner, setIsForeigner] = useState(idIsForeignerSelect);
    const [priceAssigned, setPriceAssigned] = useState('pvp_1');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordScore, setPasswordScore] = useState(0); // 0 to 4
    const strengthLabels = ['Muy débil', 'Débil', 'Aceptable', 'Buena', 'Segura'];
    const strengthColors = ['#dc3545', '#fd7e14', '#ffc107', '#0d6efd', '#198754'];

    const [createContificoClient] = useCreateContificoPersonClientMutation();
    const [register, { isLoading }] = useRegisterMutation();
    const [getContificoCtfPerson] = useGetContificoPersonDataMutation();

    const { userInfo } = useSelector((state) => state.auth);

    const { search } = useLocation();
    const searchParams = new URLSearchParams(search);
    const redirect = searchParams.get('redirect') || '/';

    const [showTCModal, setShowTCModal] = useState(false);

    const fillForm = (cPerson) => {
        fillFromContifico(
            cPerson,
            setName,
            setEmail,
            setType,
            setTaxDocId,
            setIsForeigner,
            setIsClient,
            setPriceAssigned
        );
    };

    const handlePasswordChange = (e) => {
        const pwd = e.target.value;
        setPassword(pwd);
        const result = zxcvbn(pwd);
        setPasswordScore(result.score); // 0 (weak) to 4 (strong)
    };

    useEffect(() => {
        if (userInfo) {
            navigate(redirect);
        }
    }, [userInfo, redirect, navigate]);

    useEffect(() => {
        // Simulate a click on mount
        infoButtonRef.current?.click();
    }, []);

    const submitHandler = async (e) => {
        e.preventDefault();

        if (!validDocId) {
            toast.error('Identificación Inválida');
            return;
        }

        if (zxcvbn(password).score < 2) {
            toast.error('La contraseña es demasiado débil');
            return;
        }

        const { finalDocId, finalType } = setFinalDocId(idTypeSelect, type, docId);

        if (password !== confirmPassword) {
            toast.error('Las contraseñas no coinciden');
            return;
        }

        try {
            // 1. Create Contifico client if not exists
            const success = await createContificoClientIfNotExists({
                docId,
                taxDocId,
                type,
                name,
                isForeigner,
                isClient,
                email,
                getContificoCtfPerson,
                createContificoClient,
            });

            if (!success) return;

            // 2. Register user in the application
            const res = await register({
                type: finalType,
                docId: finalDocId,
                taxDocId,
                name,
                email,
                password,
                isClient,
                isForeigner,
                priceAssigned,
            }).unwrap();

            dispatch(setCredentials({ ...res }));
            dispatch(fetchActiveGlobalDiscount());
            clearGuestId();
            
            // Force refetch of active order
            dispatch(orderApiSlice.util.invalidateTags(['Order']));
            
            // Fetch again after delay to catch background merge
            setTimeout(() => {
                dispatch(orderApiSlice.util.invalidateTags(['Order']));
            }, 1500);

            toast.success('Usuario Creado con Éxito');
            navigate(redirect);
        } catch (err) {
            toast.error(err?.data?.message || err.error || 'Error de registro');
        }
    };

    const popover = (
        <Popover id="popover-basic">
            <Popover.Header as="h3">¿Eres cliente de Comercial Santy?</Popover.Header>
            <Popover.Body>
                ¡Regístrate en segundos! Ingresa tu Número de Cédula o RUC y presiona el botón de
                búsqueda
            </Popover.Body>
        </Popover>
    );

    return (
        <FormContainer>
            <Meta title="Registro" />
            <Card className="shadow-lg border-0 rounded-lg my-5">
                <Card.Header className="text-white text-center py-4 rounded-top" style={{ backgroundColor: '#b5430f' }}>
                    <h2 className="mb-0 fw-bold">
                        <FaUserPlus className="me-2" />
                        Registro
                    </h2>
                    <OverlayTrigger trigger="click" placement="auto" overlay={popover} rootClose flip>
                        <Button
                            variant="link"
                            className="text-white position-absolute top-0 end-0 m-3"
                            ref={infoButtonRef}
                        >
                            <FaInfo />
                        </Button>
                    </OverlayTrigger>
                </Card.Header>
                <Card.Body className="p-4 p-md-5">
                    <Form onSubmit={submitHandler}>
                        {/* DocIdGroup */}
                        <div className="mb-4">
                            <DocIdGroup
                                docId={docId}
                                setDocId={setDocId}
                                type={type}
                                setType={setType}
                                isForeigner={isForeigner}
                                setIsForeigner={setIsForeigner}
                                taxDocId={taxDocId}
                                setTaxDocId={setTaxDocId}
                                fillForm={fillForm}
                                setValidDocId={setValidDocId}
                            />
                        </div>

                        <Form.Group controlId="name" className="mb-4">
                            <Form.Label column='lg' className="fw-bold">Nombre Completo</Form.Label>
                            <Form.Control
                                required
                                type="text"
                                placeholder="Ingrese su nombre completo"
                                value={name}
                                onChange={(e) => {
                                    setName(sanitizeInput(e.target.value, 'text'));
                                }}
                                className="py-2"
                            />
                        </Form.Group>

                        <Form.Group controlId="email" className="mb-4">
                            <Form.Label column='lg' className="fw-bold">Correo Electrónico</Form.Label>
                            <Form.Control
                                required
                                type="email"
                                placeholder="ejemplo@correo.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="py-2"
                            />
                        </Form.Group>

                        <Form.Group controlId="password" className="mb-4">
                            <Form.Label column='lg' className="fw-bold">Contraseña</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    required
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Ingrese su contraseña"
                                    value={password}
                                    onChange={handlePasswordChange}
                                    className="py-2 px-3 rounded-start-3 border-secondary-subtle border-end-0"
                                />
                                <Button
                                    variant="outline-secondary"
                                    className="border-secondary-subtle border-start-0 rounded-end-3 bg-white text-muted"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </Button>
                            </InputGroup>
                            {password && (
                                <div className="mt-2">
                                    <div
                                        className="progress"
                                        style={{ height: '6px' }}
                                    >
                                        <div
                                            className="progress-bar"
                                            role="progressbar"
                                            style={{
                                                width: `${(passwordScore + 1) * 20}%`,
                                                backgroundColor: strengthColors[passwordScore],
                                            }}
                                            aria-valuenow={(passwordScore + 1) * 20}
                                            aria-valuemin="0"
                                            aria-valuemax="100"
                                        ></div>
                                    </div>
                                    <small style={{ color: strengthColors[passwordScore], fontWeight: 'bold' }}>
                                        Seguridad: {strengthLabels[passwordScore]}
                                    </small>
                                </div>
                            )}
                        </Form.Group>

                        <Form.Group controlId="confirmPassword" className="mb-4">
                            <Form.Label column='lg' className="fw-bold">Confirmar Contraseña</Form.Label>
                            <InputGroup>
                                <Form.Control
                                    required
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    placeholder="Confirme su contraseña"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="py-2 px-3 rounded-start-3 border-secondary-subtle border-end-0"
                                />
                                <Button
                                    variant="outline-secondary"
                                    className="border-secondary-subtle border-start-0 rounded-end-3 bg-white text-muted"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                                </Button>
                            </InputGroup>
                        </Form.Group>

                        <div className="d-grid gap-2 mt-5">
                            <Button
                                type="submit"
                                variant="primary"
                                size="lg"
                                disabled={isLoading}
                                className="fw-bold"
                                style={{ backgroundColor: '#b5430f', borderColor: '#b5430f' }}
                            >
                                {isLoading ? (
                                    <>
                                        <Spinner animation="border" size="sm" className="me-2" /> Registrando...
                                    </>
                                ) : (
                                    <>
                                        <FaCheckCircle className="me-2" /> Registrarse
                                    </>
                                )}
                            </Button>
                        </div>

                        {isLoading && <Loader />}
                    </Form>
                </Card.Body>
                <Card.Footer className="bg-light text-center py-4">
                    <Row>
                        <Col md={6} className="mb-2 mb-md-0">
                            <small className="text-muted">
                                Al registrarte, aceptas nuestros{' '}
                                <button
                                    type="button"
                                    onClick={() => setShowTCModal(true)}
                                    className="btn btn-link p-0 align-baseline"
                                    style={{
                                        color: '#b5430f',
                                        textDecoration: 'none',
                                        fontSize: 'inherit'
                                    }}
                                >
                                    Términos y Condiciones
                                </button>
                            </small>
                        </Col>
                        <Col md={6}>
                            <small className="text-muted">
                                ¿Ya tienes cuenta?{' '}
                                <Link
                                    to={redirect ? `/login?redirect=${redirect}` : '/login'}
                                    style={{ color: '#b5430f', textDecoration: 'none', fontWeight: 'bold' }}
                                >
                                    Iniciar Sesión
                                </Link>
                            </small>
                        </Col>
                    </Row>
                </Card.Footer>
            </Card>
            <TermsAndConditionsModal
                show={showTCModal}
                handleClose={() => setShowTCModal(false)}
            />
        </FormContainer>
    );
};

export default RegisterScreen;
